import React, { useState, useMemo } from 'react';
import {
  Table, Card, Input, Button, Select, Space, Tag, Typography, Form,
  Modal, Upload, message, Segmented, Row, Col, Statistic, Alert,
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, DownloadOutlined, InboxOutlined,
} from '@ant-design/icons';
import SearchSelect from '../../../components/SearchSelect/SearchSelect';

const { Title, Text } = Typography;

// ──────────────────────────────────────────────
// Mock 数据 — TODO: 联调时替换为接口数据
// ──────────────────────────────────────────────
const CURRENT_ENTERPRISE = { id: 'ENT001', name: '深圳智创科技有限公司' };

const ENTERPRISES = [
  { id: 'ENT001', name: '深圳智创科技有限公司' },
  { id: 'ENT002', name: '杭州万物互联有限公司' },
  { id: 'ENT003', name: '北京智联科技有限公司' },
];

const PRODUCTS = [
  { id: 'PROD_Q1', name: '智能摄像机Q1' },
  { id: 'PROD_P1', name: '云台机P1' },
  { id: 'PROD_S1', name: '智能门锁S1' },
  { id: 'PROD_CAM2', name: '户外摄像机C2' },
];

const MOCK_RECORDS = [
  { uniqueId: 'UID-1003', license: 'A01ENT2604150122', createdAt: '2026-04-15 10:00:00', companyId: 'ENT001', companyName: '深圳智创科技有限公司', productId: 'PROD_Q1' },
  { uniqueId: 'UID-1006', license: 'A01ENT2604150845', createdAt: '2026-04-15 10:00:00', companyId: 'ENT001', companyName: '深圳智创科技有限公司', productId: 'PROD_Q1' },
  { uniqueId: 'UID-0901', license: 'A01ENT2603012210', createdAt: '2026-03-01 09:12:00', companyId: 'ENT001', companyName: '深圳智创科技有限公司', productId: 'PROD_P1' },
  { uniqueId: 'UID-0902', license: 'A01ENT2603019931', createdAt: '2026-03-01 09:12:00', companyId: 'ENT001', companyName: '深圳智创科技有限公司', productId: 'PROD_P1' },
  { uniqueId: 'UID-2001', license: 'A02HZW2605103312', createdAt: '2026-05-10 14:20:00', companyId: 'ENT002', companyName: '杭州万物互联有限公司', productId: 'PROD_CAM2' },
  { uniqueId: 'UID-2002', license: 'A02HZW2605107788', createdAt: '2026-05-10 14:20:00', companyId: 'ENT002', companyName: '杭州万物互联有限公司', productId: 'PROD_CAM2' },
  { uniqueId: 'UID-2003', license: 'A03BJZ2606014455', createdAt: '2026-06-01 11:05:00', companyId: 'ENT003', companyName: '北京智联科技有限公司', productId: 'PROD_S1' },
];

// 模拟文件解析得到的 unique_id（含「文件内重复」与「企业内已存在」两类重复）
const MOCK_PARSED_IDS = [
  'UID-1001', 'UID-1002', 'UID-1002', 'UID-1003', 'UID-1004',
  'UID-1005', 'UID-1006', 'UID-1007', 'UID-1008', 'UID-1009',
];

const pad2 = (n) => String(n).padStart(2, '0');

const formatNow = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
};

const formatStamp = () => {
  const d = new Date();
  return `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}${pad2(d.getHours())}${pad2(d.getMinutes())}${pad2(d.getSeconds())}`;
};

const genLicense = (companyId) => {
  const d = new Date();
  const ymd = `${String(d.getFullYear()).slice(2)}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}`;
  const code = companyId.slice(-3).toUpperCase();
  return `A01${code}${ymd}${Math.floor(1000 + Math.random() * 9000)}`;
};

const toCSV = (rows) => rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');

const triggerDownload = (filename, content) => {
  const blob = new Blob(['\ufeff' + content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const UniqueIdLicense = () => {
  const [view, setView] = useState('enterprise');
  const [records, setRecords] = useState(MOCK_RECORDS);

  // 查询条件
  const [queryType, setQueryType] = useState('uniqueId');
  const [keyword, setKeyword] = useState('');
  const [applied, setApplied] = useState({ type: 'uniqueId', keyword: '' });
  const [companyFilter, setCompanyFilter] = useState(undefined);

  // 导入弹窗
  const [modalForm] = Form.useForm();
  const [importOpen, setImportOpen] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [uploadedName, setUploadedName] = useState('');

  const filtered = useMemo(() => {
    let list = records;
    if (view === 'enterprise') {
      list = list.filter((r) => r.companyId === CURRENT_ENTERPRISE.id);
    } else if (companyFilter) {
      list = list.filter((r) => r.companyId === companyFilter);
    }
    if (applied.keyword.trim()) {
      const kw = applied.keyword.trim().toLowerCase();
      list = list.filter((r) => (applied.type === 'uniqueId' ? r.uniqueId : r.license).toLowerCase().includes(kw));
    }
    return list;
  }, [records, view, companyFilter, applied]);

  const handleSearch = () => setApplied({ type: queryType, keyword });

  const handleReset = () => {
    setQueryType('uniqueId');
    setKeyword('');
    setApplied({ type: 'uniqueId', keyword: '' });
    setCompanyFilter(undefined);
  };

  const handleViewChange = (val) => {
    setView(val);
    setCompanyFilter(undefined);
    setParsed(null);
  };

  const fetchCompanyOptions = (kw) => Promise.resolve(
    ENTERPRISES
      .filter((e) => !kw || e.name.includes(kw) || e.id.includes(kw))
      .map((e) => ({ value: e.id, label: `${e.name}（${e.id}）` })),
  );

  const openImport = () => {
    modalForm.resetFields();
    setParsed(null);
    setUploadedName('');
    setImportOpen(true);
  };

  const handleFileChange = (info) => {
    const file = info.file;
    if (!file) return;
    if (view === 'platform' && !modalForm.getFieldValue('companyId')) {
      message.warning('请先选择目标企业，再上传文件');
      return;
    }
    const companyId = view === 'platform'
      ? modalForm.getFieldValue('companyId')
      : CURRENT_ENTERPRISE.id;
    const existing = new Set(records.filter((r) => r.companyId === companyId).map((r) => r.uniqueId));
    const seen = new Set();
    const valid = [];
    const duplicates = [];
    MOCK_PARSED_IDS.forEach((uid) => {
      if (seen.has(uid)) {
        duplicates.push({ uniqueId: uid, reason: '文件内重复' });
      } else if (existing.has(uid)) {
        duplicates.push({ uniqueId: uid, reason: '企业内已存在' });
        seen.add(uid);
      } else {
        valid.push(uid);
        seen.add(uid);
      }
    });
    setUploadedName(file.name);
    setParsed({ total: MOCK_PARSED_IDS.length, valid, duplicates });
  };

  const handleGenerate = async () => {
    try {
      await modalForm.validateFields();
    } catch {
      return;
    }
    if (!parsed || parsed.valid.length === 0) {
      message.warning('没有可生成的 unique_id，请检查文件');
      return;
    }
    const companyId = view === 'platform'
      ? modalForm.getFieldValue('companyId')
      : CURRENT_ENTERPRISE.id;
    const companyName = ENTERPRISES.find((e) => e.id === companyId)?.name || CURRENT_ENTERPRISE.name;
    const productId = modalForm.getFieldValue('productId');
    const createdAt = formatNow();
    const orderName = `unique_id 生成 License—${formatStamp()}`;
    const created = parsed.valid.map((uid) => ({
      uniqueId: uid,
      license: genLicense(companyId),
      createdAt,
      companyId,
      companyName,
      productId,
    }));
    setRecords((prev) => [...created, ...prev]);
    setImportOpen(false);
    message.success(`已创建工单「${orderName}」，生成 ${created.length} 个 License`);
  };

  const handleExport = () => {
    if (filtered.length === 0) {
      message.warning('当前筛选结果为空，无可导出数据');
      return;
    }
    const header = view === 'platform'
      ? ['unique_id', 'License', '生成时间', '所属企业', '所属企业ID']
      : ['unique_id', 'License', '生成时间'];
    const rows = filtered.map((r) => (view === 'platform'
      ? [r.uniqueId, r.license, r.createdAt, r.companyName, r.companyId]
      : [r.uniqueId, r.license, r.createdAt]));
    triggerDownload(`unique_id-License映射_${formatStamp()}.csv`, toCSV([header, ...rows]));
  };

  const handleDownloadTemplate = () => {
    triggerDownload('unique_id导入模板.csv', toCSV([['unique_id'], ['UID-1001'], ['UID-1002']]));
  };

  const columns = [
    {
      title: 'unique_id',
      dataIndex: 'uniqueId',
      width: 220,
      render: (t) => <Text copyable style={{ fontFamily: 'monospace' }}>{t}</Text>,
    },
    {
      title: 'License',
      dataIndex: 'license',
      width: 240,
      render: (t) => <Text copyable style={{ fontFamily: 'monospace' }}>{t}</Text>,
    },
    { title: '生成时间', dataIndex: 'createdAt', width: 200 },
  ];
  if (view === 'platform') {
    columns.push({
      title: '所属企业',
      dataIndex: 'companyName',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.companyName}</div>
          <div style={{ fontSize: 12, color: '#8c8c8c', fontFamily: 'monospace' }}>{r.companyId}</div>
        </div>
      ),
    });
  }

  const dupColumns = [
    {
      title: '重复 unique_id',
      dataIndex: 'uniqueId',
      render: (t) => <Text style={{ fontFamily: 'monospace' }}>{t}</Text>,
    },
    {
      title: '重复原因',
      dataIndex: 'reason',
      width: 140,
      render: (t) => <Tag color="warning">{t}</Tag>,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4} style={{ margin: 0 }}>外部授权码</Title>
        <Space>
          <Text type="secondary" style={{ fontSize: 12 }}>mock 角色视角切换</Text>
          <Segmented
            value={view}
            onChange={handleViewChange}
            options={[{ label: '企业视角', value: 'enterprise' }, { label: '平台视角', value: 'platform' }]}
          />
        </Space>
      </div>

      <Alert
        type="info"
        showIcon
        closable
        style={{ marginBottom: 16 }}
        message={(
          <div>
            <div>
              导入 unique_id 后，系统会校验企业内是否重复；仅对非重复的 unique_id 批量生成 License，并建立 unique_id ↔ License 映射。
            </div>
            <div style={{ marginTop: 4 }}>
              <Text type="danger" strong>仅限不上鹤梦公有云的设备使用！！</Text>
            </div>
          </div>
        )}
      />

      <Card bordered={false} style={{ marginBottom: 16 }}>
        <Form layout="inline" style={{ rowGap: 12 }}>
          <Form.Item style={{ marginBottom: 0, flex: '1 1 320px', minWidth: 320 }}>
            <Space.Compact style={{ width: '100%' }}>
              <Select
                value={queryType}
                onChange={setQueryType}
                style={{ width: 120 }}
                options={[{ value: 'uniqueId', label: 'unique_id' }, { value: 'license', label: 'License' }]}
              />
              <Input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onPressEnter={handleSearch}
                placeholder={queryType === 'uniqueId' ? '请输入 unique_id 模糊查询' : '请输入 License 模糊查询'}
                allowClear
                style={{ width: '100%' }}
              />
            </Space.Compact>
          </Form.Item>
          {view === 'platform' && (
            <Form.Item style={{ marginBottom: 0 }}>
              <SearchSelect
                fetchOptions={fetchCompanyOptions}
                placeholder="所属企业"
                value={companyFilter}
                onChange={setCompanyFilter}
                style={{ width: 260 }}
              />
            </Form.Item>
          )}
          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>查询</Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card
        bordered={false}
        title="映射列表"
        extra={(
          <Space>
            <Button type="primary" icon={<InboxOutlined />} onClick={openImport}>导入</Button>
            <Button icon={<DownloadOutlined />} onClick={handleExport}>导出</Button>
          </Space>
        )}
      >
        <Table
          rowKey={(r) => r.uniqueId}
          columns={columns}
          dataSource={filtered}
          pagination={{ showSizeChanger: true, pageSizeOptions: [20, 50, 100], showTotal: (t) => `共 ${t} 条记录` }}
          locale={{ emptyText: '暂无 unique_id-License 映射数据' }}
        />
      </Card>

      <Modal
        title="导入 unique_id 生成 License"
        open={importOpen}
        onCancel={() => setImportOpen(false)}
        width={720}
        destroyOnClose
        footer={(
          <Space>
            <Button onClick={() => setImportOpen(false)}>取消</Button>
            <Button type="primary" onClick={handleGenerate} disabled={!parsed || parsed.valid.length === 0}>
              确认生成
            </Button>
          </Space>
        )}
      >
        <Form form={modalForm} layout="vertical" style={{ marginTop: 8 }}>
          <Row gutter={16}>
            <Col span={view === 'platform' ? 12 : 24}>
              <Form.Item name="productId" label="生产产品" rules={[{ required: true, message: '请选择生产产品' }]}>
                <Select placeholder="请选择产品" showSearch optionFilterProp="label" options={PRODUCTS.map((p) => ({ value: p.id, label: p.name }))} />
              </Form.Item>
            </Col>
            {view === 'platform' && (
              <Col span={12}>
                <Form.Item name="companyId" label="目标企业" rules={[{ required: true, message: '请选择目标企业' }]}>
                  <SearchSelect fetchOptions={fetchCompanyOptions} placeholder="请选择目标企业" />
                </Form.Item>
              </Col>
            )}
          </Row>

          <Form.Item style={{ marginBottom: 8 }}>
            <Space>
              <Text type="secondary">导入格式：Excel / CSV，单次最多 5000 条</Text>
              <Button type="link" size="small" icon={<DownloadOutlined />} style={{ padding: 0 }} onClick={handleDownloadTemplate}>
                下载模板
              </Button>
            </Space>
          </Form.Item>

          <Upload.Dragger
            accept=".xls,.xlsx,.csv"
            maxCount={1}
            beforeUpload={() => false}
            onChange={handleFileChange}
            fileList={uploadedName ? undefined : []}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
            <p className="ant-upload-hint">仅支持 .xls / .xlsx / .csv 文件</p>
          </Upload.Dragger>

          {parsed && (
            <div style={{ marginTop: 16 }}>
              <Row gutter={16}>
                <Col span={8}><Statistic title="解析总数" value={parsed.total} /></Col>
                <Col span={8}><Statistic title="可生成" value={parsed.valid.length} valueStyle={{ color: '#52c41a' }} /></Col>
                <Col span={8}><Statistic title="重复（跳过）" value={parsed.duplicates.length} valueStyle={{ color: '#faad14' }} /></Col>
              </Row>
              {parsed.duplicates.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>以下 unique_id 重复，已跳过，不生成 License：</Text>
                  <Table
                    style={{ marginTop: 8 }}
                    size="small"
                    pagination={false}
                    rowKey={(r, i) => `${r.uniqueId}-${i}`}
                    columns={dupColumns}
                    dataSource={parsed.duplicates}
                    scroll={{ y: 180 }}
                  />
                </div>
              )}
            </div>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default UniqueIdLicense;
